import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/adverse-events/+types/index.ts'
import { AcknowledgementSection } from '#app/routes/mocra+/_form+/adverse-events/components/acknowledgement-section.tsx'
import { AdverseEventContactsSection } from '#app/routes/mocra+/_form+/adverse-events/components/adverse-event-contacts-section.tsx'
import { ComplaintSystemsSection } from '#app/routes/mocra+/_form+/adverse-events/components/complaint-systems-section.tsx'
import { createAdverseEventContactDefinition } from '#app/routes/mocra+/_form+/adverse-events/components/contact-definition.ts'
import { intakeChannelDefinition } from '#app/routes/mocra+/_form+/adverse-events/components/intake-channels/definition.ts'
import {
	createIntakeChannelDraft,
	isIntakeChannelValue,
	sanitizeIntakeChannelDraft,
	type IntakeChannelDraft,
} from '#app/routes/mocra+/_form+/adverse-events/components/intake-channels/types.ts'
import { IntakeChannelsSection } from '#app/routes/mocra+/_form+/adverse-events/components/intake-channels-section.tsx'
import { MedicalReviewersSection } from '#app/routes/mocra+/_form+/adverse-events/components/medical-reviewers-section.tsx'
import {
	CONTACT_SECTIONS,
	type ContactSectionMeta,
} from '#app/routes/mocra+/_form+/adverse-events/contact-sections.ts'
import {
	ADVERSE_EVENTS_SECTION_KEY,
	AdverseEventsSchema,
	DEFAULT_VALUES,
	type AcknowledgementEntry,
	type AdverseEventContactEntry,
	type AdverseEventsFormValues,
	type ComplaintChannelEntry,
	type ComplaintSystemEntry,
} from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import { CHANNEL_CARD_OPTIONS } from '#app/routes/mocra+/_form+/adverse-events/helpers.ts'
import { normalizeAdverseEventsDraft } from '#app/routes/mocra+/_form+/adverse-events/utils.ts'
import { aeSystemDefinition } from '#app/routes/mocra+/_form+/components/ae-systems/definition.ts'
import {
	sanitizeAeSystemDraft,
	type AeSystemDraft,
} from '#app/routes/mocra+/_form+/components/ae-systems/types.ts'
import {
	type ContactCandidate,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'
import { useEntityCollection } from '#app/routes/mocra+/_form+/hooks/use-entity-collection.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import { CHANNEL_SYSTEM_FALLBACK_OPTIONS } from '#app/routes/mocra+/_form+/intake-options.ts'
import { loadSection } from '#app/routes/mocra+/_form+/intake-storage.ts'
import { type PrimaryContactsFormValues } from '#app/routes/mocra+/_form+/primary-contacts.tsx'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Adverse Event Ops' },
]

const SECTION_KEY = ADVERSE_EVENTS_SECTION_KEY
const STEP_ID = 'adverse-events'

type ChannelOptionValue = (typeof CHANNEL_CARD_OPTIONS)[number]['value']
type SystemOption = { value: string; label: string }

const MEDICAL_REVIEWER_DEFINITION = createAdverseEventContactDefinition({
	defaultRole: 'Medical reviewer',
})
const SERIOUS_EVENT_CONTACT_DEFINITION = createAdverseEventContactDefinition({
	defaultRole: 'Serious adverse event contact',
})
const FOLLOW_UP_CONTACT_DEFINITION = createAdverseEventContactDefinition({
	defaultRole: 'Follow-up contact',
})
const TREND_ALERT_CONTACT_DEFINITION = createAdverseEventContactDefinition({
	defaultRole: 'Trend alert contact',
})

const CONTACT_DEFAULT_ROLE_BY_KEY = {
	seriousEventContacts: 'Serious adverse event contact',
	followUpContacts: 'Follow-up contact',
	trendAlertContacts: 'Trend alert contact',
} as const satisfies Record<ContactSectionMeta['key'], string>

export default function AdverseEvents() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const form = useSectionForm({
		section: SECTION_KEY,
		schema: AdverseEventsSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})
	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = form

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeAdverseEventsDraft(values)
		if (normalized && !adverseEventsValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
	}, [setValues, values])

	const medicalReviewersCollection = useEntityCollection<
		AdverseEventsFormValues,
		AdverseEventContactEntry,
		ContactDraft
	>({
		form,
		fieldPath: 'medicalReviewers',
		definition: MEDICAL_REVIEWER_DEFINITION,
		getFieldErrorPaths: (index) =>
			getContactFieldErrorPaths('medicalReviewers', index),
	})

	const seriousEventContactsCollection = useEntityCollection<
		AdverseEventsFormValues,
		AdverseEventContactEntry,
		ContactDraft
	>({
		form,
		fieldPath: 'seriousEventContacts',
		definition: SERIOUS_EVENT_CONTACT_DEFINITION,
		getFieldErrorPaths: (index) =>
			getContactFieldErrorPaths('seriousEventContacts', index),
	})

	const followUpContactsCollection = useEntityCollection<
		AdverseEventsFormValues,
		AdverseEventContactEntry,
		ContactDraft
	>({
		form,
		fieldPath: 'followUpContacts',
		definition: FOLLOW_UP_CONTACT_DEFINITION,
		getFieldErrorPaths: (index) =>
			getContactFieldErrorPaths('followUpContacts', index),
	})

	const trendAlertContactsCollection = useEntityCollection<
		AdverseEventsFormValues,
		AdverseEventContactEntry,
		ContactDraft
	>({
		form,
		fieldPath: 'trendAlertContacts',
		definition: TREND_ALERT_CONTACT_DEFINITION,
		getFieldErrorPaths: (index) =>
			getContactFieldErrorPaths('trendAlertContacts', index),
	})

	const complaintSystemsCollection = useEntityCollection<
		AdverseEventsFormValues,
		ComplaintSystemEntry,
		AeSystemDraft
	>({
		form,
		fieldPath: 'complaintSystems',
		definition: aeSystemDefinition,
		getFieldErrorPaths: (index) => [
			`complaintSystems.${index}.platform`,
			`complaintSystems.${index}.name`,
			`complaintSystems.${index}.usage`,
		],
	})

	const complaintSystemItems = useMemo(
		() =>
			complaintSystemsCollection.items.map(({ draft, errors }) => ({
				draft: sanitizeAeSystemDraft(draft),
				errors,
			})),
		[complaintSystemsCollection.items],
	)

	const complaintSystemWarnings =
		complaintSystemItems.length > 0
			? []
			: ['Add at least one complaint logging system.']

	const handleAddComplaintSystem = (draft: AeSystemDraft) => {
		complaintSystemsCollection.append(draft)
	}

	const handleEditComplaintSystem = (index: number, draft: AeSystemDraft) => {
		complaintSystemsCollection.replace(index, draft)
	}

	const handleRemoveComplaintSystem = (index: number) => {
		complaintSystemsCollection.remove(index)
	}

	const intakeChannelsCollection = useEntityCollection<
		AdverseEventsFormValues,
		ComplaintChannelEntry,
		IntakeChannelDraft
	>({
		form,
		fieldPath: 'intakeChannels',
		definition: intakeChannelDefinition,
		getFieldErrorPaths: (index) => [
			`intakeChannels.${index}.ownerType`,
			`intakeChannels.${index}.ownerName`,
			`intakeChannels.${index}.system`,
		],
	})

	const selectedIntakeChannels = useMemo(
		() =>
			intakeChannelsCollection.items
				.map(({ entry, draft, errors }) => {
					if (!isIntakeChannelValue(entry.channel)) return null
					return {
						value: entry.channel,
						draft: sanitizeIntakeChannelDraft(draft),
						errors,
					}
				})
				.filter(
					(
						item,
					): item is {
						value: ChannelOptionValue
						draft: IntakeChannelDraft
						errors: readonly string[]
					} => item !== null,
				),
		[intakeChannelsCollection.items],
	)

	const intakeChannelMap = useMemo(() => {
		const map = new Map<
			ChannelOptionValue,
			{
				value: ChannelOptionValue
				draft: IntakeChannelDraft
				errors: readonly string[]
			}
		>()
		for (const item of selectedIntakeChannels) {
			map.set(item.value, item)
		}
		return map
	}, [selectedIntakeChannels])

	const intakeChannelItems = useMemo(
		() =>
			CHANNEL_CARD_OPTIONS.map((option) => {
				const entry = intakeChannelMap.get(option.value)
				return {
					value: option.value,
					draft: entry?.draft,
					errors: entry?.errors ?? [],
				}
			}),
		[intakeChannelMap],
	)

	const intakeChannelWarnings = (() => {
		const arrayErrors = getFieldErrors('intakeChannels')
		if (intakeChannelsCollection.items.length === 0) {
			return Array.from(
				new Set([
					'Confirm who owns each intake channel and where complaints land.',
					...arrayErrors,
				]),
			)
		}
		return Array.from(new Set(arrayErrors))
	})()

	const systemOptions = useMemo(() => {
		const unique = new Map<string, SystemOption>()
		for (const system of complaintSystemsCollection.items) {
			const name = sanitizeAeSystemDraft(system.draft).name.trim()
			if (!name) continue
			const lower = name.toLowerCase()
			if (!unique.has(lower)) {
				unique.set(lower, { value: name, label: name })
			}
		}
		for (const fallback of CHANNEL_SYSTEM_FALLBACK_OPTIONS) {
			if (!unique.has(fallback.value.toLowerCase())) {
				unique.set(fallback.value.toLowerCase(), {
					value: fallback.value,
					label: fallback.label,
				})
			}
		}
		return Array.from(unique.values())
	}, [complaintSystemsCollection.items])

	const handleEnsureIntakeChannel = (value: ChannelOptionValue) => {
		if (intakeChannelMap.has(value)) return
		intakeChannelsCollection.append(createIntakeChannelDraft(value))
	}

	const handleSubmitIntakeChannel = (
		value: ChannelOptionValue,
		draft: IntakeChannelDraft,
	) => {
		const sanitized = sanitizeIntakeChannelDraft({ ...draft, channel: value })
		const existingIndex = intakeChannelsCollection.items.findIndex(
			(item) => item.entry.channel === value,
		)
		if (existingIndex >= 0) {
			intakeChannelsCollection.replace(existingIndex, sanitized)
			return
		}
		intakeChannelsCollection.append(sanitized)
	}

	const handleRemoveIntakeChannel = (value: ChannelOptionValue) => {
		const existingIndex = intakeChannelsCollection.items.findIndex(
			(item) => item.entry.channel === value,
		)
		if (existingIndex >= 0) {
			intakeChannelsCollection.remove(existingIndex)
		}
	}

	const acknowledgement =
		values.acknowledgement ?? DEFAULT_VALUES.acknowledgement
	const acknowledgementWindowErrors = getFieldErrors('acknowledgement.window')
	const acknowledgementChannelErrors = getFieldErrors('acknowledgement.channel')
	const acknowledgementCustomWindowErrors = getFieldErrors(
		'acknowledgement.customWindow',
	)

	const handleAcknowledgementWindowChange = (
		next: AcknowledgementEntry['window'],
	) => {
		setFieldValue('acknowledgement.window', next)
		if (next !== 'custom') {
			setFieldValue('acknowledgement.customWindow', '')
		}
	}

	const handleAcknowledgementChannelChange = (
		next: AcknowledgementEntry['channel'],
	) => {
		setFieldValue('acknowledgement.channel', next)
	}

	const handleAcknowledgementCustomWindowChange = (next: string) => {
		setFieldValue('acknowledgement.customWindow', next)
	}

	const handleAcknowledgementCoversWeekendsChange = (next: boolean) => {
		setFieldValue('acknowledgement.coversWeekends', next)
	}

	const { contacts: directoryContacts } = useContactDirectory()
	const directory = useMemo(() => {
		const map = new Map<string, (typeof directoryContacts)[number]>()
		for (const entry of directoryContacts) {
			map.set(entry.id, entry)
		}
		return map
	}, [directoryContacts])

	const reviewerCandidates = useMemo(() => {
		const candidates: ContactCandidate[] = []
		medicalReviewersCollection.entries.forEach((contact, index) => {
			const entry = contact.contactId
				? directory.get(contact.contactId)
				: undefined
			const rawName =
				entry?.name ??
				contact.name ??
				contact.fallbackName ??
				'Medical reviewer'
			const rawEmail =
				entry?.email ?? contact.email ?? contact.fallbackEmail ?? ''
			const name = rawName.trim()
			const email = rawEmail.trim()
			if (!name && !email) return
			const phoneValue =
				entry?.phone ?? contact.phone ?? contact.fallbackPhone ?? ''
			const phone = phoneValue.trim()
			const source =
				entry?.title ??
				contact.fallbackTitle ??
				contact.role ??
				'Medical reviewer'
			const id =
				contact.contactId ??
				(email.length > 0
					? `medical-${email.toLowerCase()}`
					: `medical-reviewer-${index}`)
			candidates.push({
				id,
				name: name.length > 0 ? name : 'Medical reviewer',
				email,
				phone: phone.length > 0 ? phone : undefined,
				source,
			})
		})
		return candidates
	}, [directory, medicalReviewersCollection.entries])

	const [primaryContactsCandidates, setPrimaryContactsCandidates] = useState<
		ContactCandidate[]
	>([])

	useEffect(() => {
		const stored = loadSection<PrimaryContactsFormValues>('primaryContacts')
		if (!stored) {
			setPrimaryContactsCandidates([])
			return
		}
		const candidates = stored.contacts
			.map(
				(contact, index): ContactCandidate => ({
					id: contact.id ? `primary-${contact.id}` : `primary-index-${index}`,
					name: contact.name.trim(),
					email: contact.email.trim(),
					phone: contact.phone.trim() || undefined,
					source: formatSourceLabel(contact.title, contact.email),
				}),
			)
			.filter(
				(candidate) => candidate.name.length > 0 || candidate.email.length > 0,
			)
		setPrimaryContactsCandidates(candidates)
	}, [])

	const escalationCandidates = useMemo(() => {
		const combined = [...reviewerCandidates, ...primaryContactsCandidates]
		const unique = new Map<string, ContactCandidate>()
		for (const candidate of combined) {
			if (!candidate.email && !candidate.name) continue
			const key = `${candidate.name}|${candidate.email}|${candidate.phone ?? ''}`
			if (!unique.has(key)) {
				unique.set(key, candidate)
			}
		}
		return Array.from(unique.values())
	}, [primaryContactsCandidates, reviewerCandidates])

	const contactCollections = {
		seriousEventContacts: seriousEventContactsCollection,
		followUpContacts: followUpContactsCollection,
		trendAlertContacts: trendAlertContactsCollection,
	} as const

	return (
		<Page
			title="Adverse Event Ops"
			description="Capture how complaints arrive, where they are logged, who provides medical judgement, and how teams escalate serious cases."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />

				<ComplaintSystemsSection
					systems={complaintSystemItems}
					warnings={complaintSystemWarnings}
					onAdd={handleAddComplaintSystem}
					onEdit={handleEditComplaintSystem}
					onRemove={handleRemoveComplaintSystem}
				/>

				<Divider />

				<IntakeChannelsSection
					channels={intakeChannelItems}
					warnings={intakeChannelWarnings}
					systemOptions={systemOptions}
					onEnsureChannel={handleEnsureIntakeChannel}
					onSubmitChannel={handleSubmitIntakeChannel}
					onRemoveChannel={handleRemoveIntakeChannel}
				/>

				<Divider />

				<AcknowledgementSection
					value={acknowledgement}
					defaultWindow={DEFAULT_VALUES.acknowledgement.window}
					onWindowChange={handleAcknowledgementWindowChange}
					onChannelChange={handleAcknowledgementChannelChange}
					onCoversWeekendsChange={handleAcknowledgementCoversWeekendsChange}
					onCustomWindowChange={handleAcknowledgementCustomWindowChange}
					windowErrors={acknowledgementWindowErrors}
					channelErrors={acknowledgementChannelErrors}
					customWindowErrors={acknowledgementCustomWindowErrors}
				/>

				<Divider />

				<MedicalReviewersSection
					items={medicalReviewersCollection.items}
					onAdd={medicalReviewersCollection.append}
					onEdit={medicalReviewersCollection.replace}
					onRemove={medicalReviewersCollection.remove}
					listErrors={getFieldErrors('medicalReviewers')}
					candidates={reviewerCandidates}
				/>

				<Divider />

				{CONTACT_SECTIONS.map((section, index) => {
					const collection = contactCollections[section.key]
					return (
						<Fragment key={section.key}>
							<AdverseEventContactsSection
								items={collection.items}
								onAdd={collection.append}
								onEdit={collection.replace}
								onRemove={collection.remove}
								section={section}
								defaultRole={CONTACT_DEFAULT_ROLE_BY_KEY[section.key]}
								listErrors={getFieldErrors(section.key)}
								candidates={escalationCandidates}
							/>
							{index < CONTACT_SECTIONS.length - 1 ? <Divider /> : null}
						</Fragment>
					)
				})}

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}
				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function formatSourceLabel(title?: string, email?: string): string {
	const trimmedTitle = title?.trim() ?? ''
	const trimmedEmail = email?.trim() ?? ''
	if (trimmedTitle && trimmedEmail) return `${trimmedTitle} · ${trimmedEmail}`
	if (trimmedTitle) return trimmedTitle
	return trimmedEmail
}

function getContactFieldErrorPaths(
	base: string,
	index: number,
): readonly string[] {
	const prefix = `${base}.${index}`
	return [
		prefix,
		`${prefix}.role`,
		`${prefix}.name`,
		`${prefix}.email`,
		`${prefix}.contactId`,
	]
}

function adverseEventsValuesEqual(
	left: AdverseEventsFormValues,
	right: AdverseEventsFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}
